import {
  Badge,
  Box,
  Button as ButtonBase,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FiBell } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { dataHoraIsoParaBr, dataIsoParaBr } from '../../helpers/dataBr';
import { rotuloTipoCirurgia } from '../../service/cirurgiaDoPedido';
import {
  INICIO_ALERTA_ERRO_INTEGRACAO,
  errosIntegracaoDoAlerta,
  lerErrosVistos,
  salvarErrosVistos,
} from '../../service/errosIntegracao';
import { parseErroIntegracao } from '../../utils/parseErroIntegracao';

// Cast para evitar o TS2590 ("union type too complex") do Chakra.
const Button = ButtonBase as React.ComponentType<any>;

interface Props {
  /** Todos os pedidos já filtrados pelas filiais do usuário (os da Lista de Pedidos). */
  pedidos: any[];
  /** false enquanto a primeira carga não chegou — senão tudo pareceria "novo". */
  pronto: boolean;
  onVerTodos: () => void;
}

function quando(pedido: any): string {
  const { data, hora } = dataHoraIsoParaBr(pedido?.data_emissao);
  const horaEmissao = String(pedido?.hora_emissao || '').substring(0, 5);
  return [data, horaEmissao || hora].filter(Boolean).join(' ');
}

/**
 * Sininho dos pedidos que o TOTVS recusou (ERRO_INTEGRACAO). O número é de
 * erros que a pessoa ainda não abriu; clicar num deles leva direto ao
 * detalhe do pedido, com o motivo, os materiais e os dados da cirurgia.
 */
export function SinoErroIntegracao({ pedidos, pronto, onVerTodos }: Props) {
  const { user } = useAuth();
  const history = useHistory();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const usuarioId = String(user?.id || '');
  const [vistos, setVistos] = useState<Set<string>>(() => lerErrosVistos(usuarioId));
  const conhecidos = useRef<Set<string> | null>(null);

  const erros = useMemo(() => errosIntegracaoDoAlerta(pedidos), [pedidos]);
  const naoVistos = erros.filter((p) => !vistos.has(p.pedido_id)).length;

  // A lista se recarrega sozinha a cada 3 min. Erro que aparece entre uma
  // carga e outra ganha um aviso, para a logística não depender de olhar o sino.
  useEffect(() => {
    if (!pronto) return;
    const ids = new Set<string>(erros.map((p) => p.pedido_id));
    const anteriores = conhecidos.current;
    // Acumula em vez de trocar: o pedido com erro que o backend tenta reenviar
    // (cron de 15 min ou o botão "Integrar pedidos") passa uns segundos em
    // PROCESSANDO e some desta lista; se falhar de novo ele volta, e não é novo.
    const acumulados = new Set<string>(anteriores || []);
    ids.forEach((id) => acumulados.add(id));
    conhecidos.current = acumulados;
    if (!anteriores) return;

    const novos = erros.filter((p) => !anteriores.has(p.pedido_id));
    if (novos.length === 0) return;
    toast({
      title:
        novos.length === 1
          ? 'NOVO PEDIDO COM ERRO DE INTEGRAÇÃO'
          : `${novos.length} NOVOS PEDIDOS COM ERRO DE INTEGRAÇÃO`,
      description: novos
        .map((p) => p?.clientes?.fantasia || 'Cliente não informado')
        .join(', '),
      status: 'warning',
      duration: 10000,
      isClosable: true,
      position: 'top-right',
    });
  }, [erros, pronto, toast]);

  function marcarVistos(ids: string[]) {
    const proximos = new Set(vistos);
    ids.forEach((id) => proximos.add(id));
    setVistos(proximos);
    salvarErrosVistos(usuarioId, proximos, erros);
  }

  function abrir(pedido: any) {
    marcarVistos([pedido.pedido_id]);
    onClose();
    history.push(`/listar/pedido/${pedido.pedido_id}`);
  }

  function verTodos() {
    onClose();
    onVerTodos();
  }

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottom-end"
      isLazy
    >
      <PopoverTrigger>
        <Button
          variant="ghost"
          position="relative"
          aria-label="Pedidos com erro de integração"
          title="Pedidos com erro de integração"
          _focus={{ boxShadow: 'none' }}
        >
          <FiBell size={20} />
          {naoVistos > 0 && (
            <Box
              position="absolute"
              top="0"
              right="0"
              minW="18px"
              h="18px"
              px="1"
              borderRadius="full"
              bg="red.500"
              color="white"
              fontSize="11px"
              fontWeight="bold"
              lineHeight="18px"
              textAlign="center"
            >
              {naoVistos > 99 ? '99+' : naoVistos}
            </Box>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        w={{ base: '90vw', md: '420px' }}
        bg="gray.800"
        borderColor="gray.700"
        _focus={{ boxShadow: 'none' }}
      >
        <PopoverArrow bg="gray.800" />
        <PopoverHeader borderColor="gray.700">
          <Text fontWeight="bold" color="gray.50">
            Erros de integração ({erros.length})
          </Text>
          <Text fontSize="xs" color="gray.400">
            Desde {dataIsoParaBr(INICIO_ALERTA_ERRO_INTEGRACAO)}, nas filiais em que
            você tem acesso
          </Text>
        </PopoverHeader>

        <PopoverBody p={0} maxH="380px" overflowY="auto">
          {erros.length === 0 ? (
            <Text p={4} fontSize="sm" color="gray.400">
              Nenhum pedido com erro de integração.
            </Text>
          ) : (
            erros.map((pedido) => {
              const novo = !vistos.has(pedido.pedido_id);
              const motivo = parseErroIntegracao(pedido?.payload_recebido)?.motivo;
              return (
                <Box
                  key={pedido.pedido_id}
                  as="button"
                  type="button"
                  w="100%"
                  textAlign="left"
                  px={4}
                  py={3}
                  borderBottom="1px solid"
                  borderColor="gray.700"
                  bg={novo ? 'gray.700' : 'transparent'}
                  _hover={{ bg: 'gray.600' }}
                  onClick={() => abrir(pedido)}
                >
                  <Flex align="center" justify="space-between">
                    <Text fontSize="sm" fontWeight="bold" color="gray.50" noOfLines={1}>
                      {pedido?.clientes?.fantasia || 'Cliente não informado'}
                    </Text>
                    {novo && (
                      <Badge colorScheme="red" ml={2} flexShrink={0}>
                        NOVO
                      </Badge>
                    )}
                  </Flex>
                  <Text fontSize="xs" color="gray.400" noOfLines={1}>
                    {[
                      rotuloTipoCirurgia(pedido?.tipo_pedido),
                      pedido?.filial_nome,
                      pedido?.colaboradores?.nome,
                      quando(pedido),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Text>
                  {motivo && (
                    <Text fontSize="xs" color="red.300" noOfLines={2} mt={1}>
                      {motivo}
                    </Text>
                  )}
                </Box>
              );
            })
          )}
        </PopoverBody>

        {erros.length > 0 && (
          <PopoverFooter borderColor="gray.700">
            <Flex justify="space-between">
              <Button
                size="sm"
                variant="ghost"
                isDisabled={naoVistos === 0}
                onClick={() => marcarVistos(erros.map((p) => p.pedido_id))}
                _focus={{ boxShadow: 'none' }}
              >
                Marcar todos como vistos
              </Button>
              <Button
                size="sm"
                colorScheme="orange"
                onClick={verTodos}
                _focus={{ boxShadow: 'none' }}
              >
                Ver na lista
              </Button>
            </Flex>
          </PopoverFooter>
        )}
      </PopoverContent>
    </Popover>
  );
}
